import { useCallback, useEffect, useState } from 'react';
import { checkProjectFileNameConflicts } from '@/entities/project-file';
import { logger } from '@/lib/utils/logger';
import { buildItems, isResolved, toResolved } from '../lib/resolution';
import type { IntakeItem, ResolutionOption, ResolvedUpload } from './types';

type Phase = 'idle' | 'checking' | 'resolving';

interface UseProjectFileUploadGuardOptions {
  projectId: string;
  folderId?: string;
  onResolved: (items: ResolvedUpload[]) => void;
}

export interface ProjectFileUploadGuardReturn {
  checking: boolean;
  isResolving: boolean;
  items: IntakeItem[];
  canConfirm: boolean;
  submit: (files: File[]) => void;
  updateResolutionOption: (id: string, choice: ResolutionOption) => void;
  rename: (id: string, newName: string) => void;
  remove: (id: string) => void;
  confirm: () => void;
  cancel: () => void;
}

export function useProjectFileUploadGuard(
  options: UseProjectFileUploadGuardOptions,
): ProjectFileUploadGuardReturn {
  const { projectId, folderId, onResolved } = options;
  const [phase, setPhase] = useState<Phase>('idle');
  const [items, setItems] = useState<IntakeItem[]>([]);

  const submit = useCallback(
    (files: File[]) => {
      if (files.length === 0) return;
      setPhase('checking');

      void (async () => {
        try {
          const { files: results } = await checkProjectFileNameConflicts(projectId, {
            fileNames: [...new Set(files.map((f) => f.name))],
            folderId: folderId ?? null,
          });
          const intake = buildItems(files, results);

          //if there are no conflicts, then resolve and return immediately
          if (intake.every((i) => i.existing == null)) {
            onResolved(intake.map((i) => ({ kind: 'new', file: i.file })));
            setPhase('idle');
            return;
          }

          //otherwise enter resolving phase, allowing the user to choose the resolution strategy per conflict
          setItems(intake);
          setPhase('resolving');
        } catch (error) {
          // If the check fails, then continue to resolution phase.
          logger.error('[project-file-upload-guard] conflict check failed, falling back', {
            error,
          });
          onResolved(files.map((file) => ({ kind: 'new', file })));
          setPhase('idle');
        }
      })();
    },
    [projectId, folderId, onResolved],
  );

  /**
   * Updates the resolution option for a file. Switching to "version" discards
   * any pending rename — a versioned upload reuses the existing file's name.
   */
  const updateResolutionOption = useCallback((id: string, option: ResolutionOption) => {
    setItems((prev) =>
      prev.map((item) =>
        item.id === id
          ? { ...item, option, newName: option === 'version' ? item.file.name : item.newName }
          : item,
      ),
    );
  }, []);

  /**
   * Renames a file to avoid conflicts
   */
  const rename = useCallback((id: string, newName: string) => {
    setItems((prev) => prev.map((item) => (item.id === id ? { ...item, newName } : item)));
  }, []);

  /** Drop a file from the batch entirely */
  const remove = useCallback((id: string) => {
    setItems((prev) => prev.filter((item) => item.id !== id));
  }, []);

  // Removing the last file leaves nothing to upload
  useEffect(() => {
    if (phase === 'resolving' && items.length === 0) setPhase('idle');
  }, [phase, items.length]);

  const confirm = useCallback(() => {
    onResolved(toResolved(items));
    setItems([]);
    setPhase('idle');
  }, [items, onResolved]);

  const cancel = useCallback(() => {
    setItems([]);
    setPhase('idle');
  }, []);

  const canConfirm = items.every(isResolved);

  return {
    checking: phase === 'checking',
    isResolving: phase === 'resolving',
    items,
    canConfirm,
    submit,
    updateResolutionOption,
    rename,
    remove,
    confirm,
    cancel,
  };
}
